import { useEffect, useState, useRef, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Loader2, AlertCircle, ArrowLeft, Phone, ShieldCheck } from "lucide-react";
import { getStripeConfig, createEmbeddedCheckout, getPaymentStatus } from "@/lib/api";
import { CONTACT } from "@/data/site";

let cachedStripe = null;

const DEPOSIT = 50;
const PROCESSING_FEE_PCT = 0.03;

export const DepositCheckout = ({ leadId, onPaid, onBack }) => {
  const fee = Math.round(DEPOSIT * PROCESSING_FEE_PCT * 100) / 100;
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState("");
  const sessionRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const cfg = await getStripeConfig();
        if (!cfg.publishable_key) throw new Error("Missing publishable key");
        if (!cachedStripe) cachedStripe = loadStripe(cfg.publishable_key);
        const { client_secret, session_id } = await createEmbeddedCheckout(leadId, 0);
        sessionRef.current = session_id;
        setStripePromise(cachedStripe);
        setClientSecret(client_secret);
      } catch (e) {
        setError("We couldn't start secure checkout. Your booking is saved — please call us to pay the deposit.");
      }
    })();
  }, [leadId]);

  const handleComplete = useCallback(async () => {
    try { await getPaymentStatus(sessionRef.current); } catch { /* status poll best-effort */ }
    onPaid?.();
  }, [onPaid]);

  if (error) {
    return (
      <div className="px-6 py-10 text-center" data-testid="deposit-checkout-error">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-navy font-medium mb-4">{error}</p>
        <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-leaf text-white font-medium"><Phone className="w-4 h-4" /> Call {CONTACT.phone}</a>
        <div className="mt-4"><button onClick={onBack} className="text-sm text-muted-foreground hover:text-navy inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button></div>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <div className="px-6 py-16 flex flex-col items-center justify-center gap-3" data-testid="deposit-checkout-loading">
        <Loader2 className="w-8 h-8 animate-spin text-leaf" />
        <p className="text-sm text-muted-foreground">Loading secure checkout…</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 pb-6" data-testid="deposit-embedded-checkout">
      <button onClick={onBack}
        className="text-sm text-muted-foreground hover:text-navy inline-flex items-center gap-1 mb-3" data-testid="deposit-back-btn"><ArrowLeft className="w-4 h-4" /> Back to booking</button>
      <div className="flex items-center gap-2 text-sm text-leaf mb-3" data-testid="deposit-summary"><ShieldCheck className="w-4 h-4" /> Secure ${DEPOSIT} deposit + ${fee.toFixed(2)} processing fee (3%) — deposit applied to your total</div>
      <div className="rounded-2xl overflow-hidden border border-border bg-white">
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret: async () => clientSecret, onComplete: handleComplete }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );
};
